import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { auth, db } from '../firebase';
import { User, Settings } from 'lucide-react';
import toast from 'react-hot-toast';
import AppLayout from './layout/AppLayout';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

interface UserProfile {
  displayName: string;
  title: string;
  department: string;
  email: string;
  phone: string;
  timeZone: string;
  notifications: {
    email: boolean;
    push: boolean;
    desktop: boolean;
  };
  subscription: {
    plan: string;
    status: string;
    validUntil: string;
  };
}

export default function ProfilePage() {
  useDocumentTitle('Profile');
  
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<UserProfile>({
    displayName: '',
    title: '',
    department: '',
    email: auth.currentUser?.email || '',
    phone: '',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    notifications: {
      email: true,
      push: true,
      desktop: true,
    },
    subscription: {
      plan: 'Free',
      status: 'active',
      validUntil: new Date().toISOString(),
    },
  });

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          navigate('/');
          return;
        }

        const docRef = doc(db, 'users', user.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const userData = docSnap.data();
          // Merge existing data with defaults to ensure all fields exist
          setProfile(prevProfile => ({
            ...prevProfile,
            ...userData,
            // Map fullName from signup to displayName for profile
            displayName: userData.displayName || userData.fullName || '',
            email: user.email || prevProfile.email // Ensure email is always set from auth
          }));
        } else {
          // Initialize new user profile with data from auth
          const initialProfile = {
            ...profile,
            displayName: user.displayName || '',
            email: user.email || '',
          };
          await setDoc(docRef, initialProfile);
          setProfile(initialProfile);
        }
      } catch (error) {
        console.error('Error loading profile:', error);
        toast.error('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      const user = auth.currentUser;
      if (!user) return;

      await setDoc(doc(db, 'users', user.uid), {
        ...profile,
        updatedAt: new Date().toISOString()
      });
      
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
      toast.error('Failed to log out');
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-text-secondary">Loading...</div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-surface-light rounded-xl shadow-sm">
            {/* Header */}
            <div className="p-6 border-b border-border-light">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center">
                  <User className="w-8 h-8 text-primary-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-semibold text-text-primary">Profile Settings</h1>
                  <p className="text-text-secondary">Manage your account preferences and settings</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-8">
              {/* Personal Information */}
              <section>
                <h2 className="text-lg font-semibold text-text-primary mb-4">Personal Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={profile.displayName}
                      onChange={(e) => setProfile({ ...profile, displayName: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                               focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      Title
                    </label>
                    <input
                      type="text"
                      value={profile.title}
                      onChange={(e) => setProfile({ ...profile, title: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                               focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      Department
                    </label>
                    <input
                      type="text"
                      value={profile.department}
                      onChange={(e) => setProfile({ ...profile, department: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                               focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={profile.phone}
                      onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                               focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                    />
                  </div>
                </div>
              </section>

              {/* Notification Preferences */}
              <section>
                <h2 className="text-lg font-semibold text-text-primary mb-4">Notification Preferences</h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-text-primary">Email Notifications</h3>
                      <p className="text-sm text-text-secondary">Receive updates via email</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={profile.notifications.email}
                        onChange={(e) => setProfile({
                          ...profile,
                          notifications: { ...profile.notifications, email: e.target.checked }
                        })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-border-light peer-focus:outline-none rounded-full peer 
                                  peer-checked:after:translate-x-full peer-checked:after:border-white 
                                  after:content-[''] after:absolute after:top-[2px] after:left-[2px] 
                                  after:bg-white after:border-gray-300 after:border after:rounded-full 
                                  after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500">
                      </div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-text-primary">Push Notifications</h3>
                      <p className="text-sm text-text-secondary">Receive mobile push notifications</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={profile.notifications.push}
                        onChange={(e) => setProfile({
                          ...profile,
                          notifications: { ...profile.notifications, push: e.target.checked }
                        })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-border-light peer-focus:outline-none rounded-full peer 
                                  peer-checked:after:translate-x-full peer-checked:after:border-white 
                                  after:content-[''] after:absolute after:top-[2px] after:left-[2px] 
                                  after:bg-white after:border-gray-300 after:border after:rounded-full 
                                  after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500">
                      </div>
                    </label>
                  </div>
                </div>
              </section>

              {/* Subscription */}
              <section>
                <h2 className="text-lg font-semibold text-text-primary mb-4">Subscription</h2>
                <div className="bg-background rounded-lg p-4 border border-border-light">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-text-primary">Current Plan</h3>
                      <p className="text-sm text-text-secondary">{profile.subscription.plan}</p>
                    </div>
                    <button
                      onClick={() => navigate('/upgrade')}
                      className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 
                               transition-colors"
                    >
                      Upgrade Plan
                    </button>
                  </div>
                </div>
              </section>

              {/* Actions */}
              <div className="flex items-center justify-between pt-6 border-t border-border-light">
                <button
                  onClick={handleLogout}
                  className="px-4 py-2 text-error-DEFAULT hover:text-error-dark transition-colors"
                >
                  Log Out
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 
                           transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}